import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Loader2,
  MapPin,
  Search,
  Ticket,
  User,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import type { SearchHit } from '../lib/types';

/**
 * One box, in the header, from any page.
 *
 * ## Why it is here and not on each list
 *
 * Every list already filters itself, which is useful once you know which list
 * to open. Somebody holding a phone number or half a reference does not — that
 * is the thing they are trying to find out. So this searches across kinds and
 * labels each answer with what it is.
 *
 * ## Why it does not search as you type, quite
 *
 * Three hundred milliseconds after you stop. Firing on every keystroke turns
 * "Bosibori" into eight queries of which seven are thrown away, and on a Kisii
 * connection the answers arrive out of order and the list flickers between
 * them. The pause is long enough to be one query and short enough that nobody
 * notices they waited.
 *
 * ## What it deliberately does not do
 *
 * Widen anybody's access. The endpoint behind it applies the same capability
 * checks as the screens and the same own-records rule for technicians — this
 * is a faster route to what you already have, never a wider one.
 */

const ICONS: Record<SearchHit['kind'], typeof Ticket> = {
  ticket: Ticket,
  customer: Users,
  contract: FileText,
  meter: Zap,
  site: MapPin,
  user: User,
};

const KIND_LABEL: Record<SearchHit['kind'], string> = {
  ticket: 'Ticket',
  customer: 'Customer',
  contract: 'Contract',
  meter: 'Meter',
  site: 'Site',
  user: 'Person',
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);

  /* Settle before asking. See the note above. */
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(raw.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [raw]);

  const results = useQuery({
    queryKey: ['search', query],
    queryFn: () => api.search(query),
    enabled: query.length >= 2,
    /*
     * The same two letters five minutes apart are the same answer. Without
     * this, clearing the box and typing it again is a fresh round trip for
     * something already on the machine.
     */
    staleTime: 30_000,
  });

  const hits = query.length >= 2 ? (results.data ?? []) : [];

  /* Clicking anywhere else closes it, which is what people expect of a menu. */
  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  /*
   * `/` focuses it, the way every tool people already use does — but never
   * while they are typing into something else, or a slash in a customer's
   * address would teleport the cursor into the search box mid-sentence.
   */
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable;

      if (event.key === '/' && !typing) {
        event.preventDefault();
        field.current?.focus();
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, []);

  const go = (hit: SearchHit) => {
    setOpen(false);
    setRaw('');
    navigate(hit.href);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      field.current?.blur();
      return;
    }
    if (!hits.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((i) => (i + 1) % hits.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((i) => (i - 1 + hits.length) % hits.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const hit = hits[highlight];
      if (hit) go(hit);
    }
  };

  return (
    <div ref={box} className="relative w-full max-w-xl">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        ref={field}
        value={raw}
        onChange={(event) => {
          setRaw(event.target.value);
          setHighlight(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search tickets, customers, contracts…"
        aria-label="Search everything"
        aria-expanded={open && query.length >= 2}
        role="combobox"
        aria-controls="global-search-results"
        className="input h-10 w-full pl-9 pr-9"
      />

      {results.isFetching && (
        <Loader2
          className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      )}

      {open && query.length >= 2 && (
        <div
          id="global-search-results"
          role="listbox"
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-[26rem] overflow-y-auto rounded-xl border border-border bg-card p-1 shadow-lg"
        >
          {hits.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {results.isFetching ? 'Looking…' : `Nothing matches “${query}”.`}
            </p>
          ) : (
            hits.map((hit, index) => {
              const Icon = ICONS[hit.kind];
              return (
                <button
                  key={`${hit.kind}-${hit.href}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => go(hit)}
                  className={`flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left ${
                    index === highlight ? 'bg-muted' : ''
                  }`}
                >
                  <Icon
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{hit.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {KIND_LABEL[hit.kind]} · {hit.detail}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
